/**
 * Владелец статьи — человек ЛИБО отдел (поле «Владелец» в свойствах статьи).
 * В одном Select значение отдела кодируется как `dept:<код>`, человека — его id.
 */

const DEPT_PREFIX = 'dept:';

/** Значение Select для отдела. */
export const ownerDeptValue = (code: string): string => `${DEPT_PREFIX}${code}`;

/** Разбор значения Select: отдел, человек или «не выбрано». */
export type OwnerChoice = { kind: 'dept'; code: string } | { kind: 'user'; id: string } | { kind: 'none' };

export function parseOwnerValue(value: unknown): OwnerChoice {
  if (typeof value !== 'string' || !value) return { kind: 'none' };
  if (value.startsWith(DEPT_PREFIX)) {
    const code = value.slice(DEPT_PREFIX.length);
    return code ? { kind: 'dept', code } : { kind: 'none' };
  }
  return { kind: 'user', id: value };
}

/** Текущее значение Select по состоянию редактора: отдел главнее (у статьи не бывает обоих). */
export function ownerSelectValue(ownerId: string | null, ownerDept: string | null): string | undefined {
  if (ownerDept) return ownerDeptValue(ownerDept);
  return ownerId ?? undefined;
}

/**
 * Что отправить на сохранение. Всегда обе пары, выбранный отдел вытесняет человека
 * (null — «снять»): так бэкенд заменяет прежнего владельца, а не оставляет двух.
 */
export function ownerPayload(ownerId: string | null, ownerDept: string | null): { owner_id: string | null; owner_dept: string | null } {
  return ownerDept ? { owner_id: null, owner_dept: ownerDept } : { owner_id: ownerId, owner_dept: null };
}

/** Подпись владельца-отдела: «Отдел: Кухня (бренд-шеф)». */
export const ownerDeptName = (label: string): string => `Отдел: ${label}`;

/** Отдел из списка `GET /wiki/editor/departments`. */
export interface OwnerDeptOption {
  code: string;
  label: string;
  peopleCount: number;
}

/** Человек из `GET /wiki/editor/users`. */
export interface OwnerUserOption {
  id: string;
  fullName: string;
}

export interface OwnerSelectOption {
  value: string;
  /** Что видно в списке. */
  label: string;
  /** Что видно в закрытом поле (у отдела — с приставкой «Отдел: »). */
  short: string;
  disabled?: boolean;
}

export interface OwnerSelectGroup {
  label: string;
  options: OwnerSelectOption[];
}

/**
 * Группы для Select: «Отделы» (фильтруются по тексту поиска здесь же — серверного поиска у них
 * нет) и «Люди» (уже отфильтрованы сервером). Выбранное значение, которого нет в списке
 * (отдел выключили, человек вне первых 20 по поиску), добавляется в свою группу.
 */
export function buildOwnerOptions(args: {
  depts: OwnerDeptOption[];
  users: OwnerUserOption[];
  search: string;
  current: { ownerId: string | null; ownerDept: string | null; name: string | null };
}): OwnerSelectGroup[] {
  const q = args.search.trim().toLowerCase();
  const deptOptions: OwnerSelectOption[] = args.depts
    .filter((d) => !q || d.label.toLowerCase().includes(q))
    .map((d) => ({
      value: ownerDeptValue(d.code),
      label: d.peopleCount === 0 ? `${d.label} — в отделе никого нет` : d.label,
      short: ownerDeptName(d.label),
      // Отдел без людей выбрать нельзя: напоминание некому получить.
      disabled: d.peopleCount === 0,
    }));
  const userOptions: OwnerSelectOption[] = args.users.map((u) => ({ value: u.id, label: u.fullName, short: u.fullName }));
  const { ownerId, ownerDept, name } = args.current;
  if (ownerDept && !args.depts.some((d) => d.code === ownerDept)) {
    const label = name?.replace(/^Отдел:\s*/, '') || ownerDept;
    deptOptions.unshift({ value: ownerDeptValue(ownerDept), label, short: ownerDeptName(label), disabled: true });
  } else if (ownerId && !args.users.some((u) => u.id === ownerId)) {
    userOptions.unshift({ value: ownerId, label: name ?? 'Владелец', short: name ?? 'Владелец' });
  }
  return [
    ...(deptOptions.length ? [{ label: 'Отделы', options: deptOptions }] : []),
    ...(userOptions.length ? [{ label: 'Люди', options: userOptions }] : []),
  ];
}

/** Подсказка под полем «Владелец». */
export function ownerHint(ownerDept: string | null): string {
  return ownerDept
    ? 'Напоминания о проверке получат руководитель и участники отдела.'
    : 'Отвечает за актуальность: ему придут напоминания о проверке.';
}
